import { Box } from '@mui/material';
import {
  CustomAutoComplete,
  CustomTextField,
  HorizontalForm,
  renderTwoLineOption,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import MaterialScopeFilters from '../../components/MaterialScopeFilters';
import {
  INTERNAL_ORDER_FILTER_TEXTS as TEXTS,
  ORDER_STATUS_LABELS,
} from '../constants/internalOrdersConstants';
import {
  getProjectLabelFromRecord,
  sortProjectOptions,
} from '../utils/internalOrdersUtils';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';
import { toProjectOptions } from '../../utils/toProjectOptions';
import { getFilterFieldSx } from '../../utils/getFilterFieldSx';

const STATUS_OPTIONS = Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const toolbarSx = {
  px: 2.5,
  py: 1.5,
  gap: 1.25,
  alignItems: 'center',
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
  overflowX: 'visible',
};

/**
 * Toolbar de filtros de pedidos técnicos en una sola fila.
 * @param {Object} props
 * @param {Object} props.filters Filtros vigentes.
 * @param {Array<Object>} props.warehouses Depósitos.
 * @param {Array<Object>} props.projects Proyectos.
 * @param {Function} props.onFilterChange Recibe el evento de los campos de texto.
 * @param {Function} props.onSelectChange Recibe el evento de los autocompletar.
 * @param {Object} props.orderFilterOptions Opciones de número y creador buscadas en el servidor.
 * @param {Object} props.scopeOptions Búsqueda de materiales (`useMaterialScopeOptions`).
 * @returns {JSX.Element}
 */
const InternalOrdersFilters = ({
  filters,
  warehouses = [],
  projects = [],
  onFilterChange,
  onSelectChange,
  orderFilterOptions,
  scopeOptions,
}) => (
  <Box sx={{ backgroundColor: 'background.paper', borderBottom: '1px solid #ccc' }}>
    <HorizontalForm withBorder={false} sx={toolbarSx}>
      <CustomAutoComplete
        label={TEXTS.status}
        name="status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={onSelectChange}
        sx={getFilterFieldSx()}
      />
      <CustomAutoComplete
        label={TEXTS.warehouse}
        name="uuid_destination_warehouse"
        value={filters.uuid_destination_warehouse}
        options={toWarehouseOptions(warehouses)}
        renderOption={renderTwoLineOption}
        inputPlaceholder={TEXTS.search}
        ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
        onChange={onSelectChange}
        sx={getFilterFieldSx()}
      />
      <CustomAutoComplete
        label={TEXTS.project}
        name="uuid_project"
        value={filters.uuid_project}
        options={toProjectOptions(sortProjectOptions(projects), getProjectLabelFromRecord)}
        inputPlaceholder={TEXTS.search}
        ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
        onChange={onSelectChange}
        sx={getFilterFieldSx(1.5, 170)}
      />
      <CustomTextField
        label={TEXTS.dateFrom}
        name="date_from"
        type="date"
        value={filters.date_from}
        onChange={onFilterChange}
        shrinkLabel
        sx={getFilterFieldSx()}
      />
      <CustomTextField
        label={TEXTS.dateTo}
        name="date_to"
        type="date"
        value={filters.date_to}
        onChange={onFilterChange}
        shrinkLabel
        sx={getFilterFieldSx()}
      />
      <CustomAutoComplete
        label={TEXTS.exactNumber}
        name="order_number"
        value={filters.order_number || ''}
        options={orderFilterOptions.numbers}
        inputPlaceholder={TEXTS.search}
        filterOptions={(options) => options}
        ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
        onChange={onSelectChange}
        onInputChange={(_, value, reason) => {
          if (reason !== 'reset') orderFilterOptions.searchNumber(value);
        }}
        sx={getFilterFieldSx(0.8, 110)}
      />
      <CustomAutoComplete
        label={TEXTS.createdBy}
        name="created_by"
        value={filters.created_by || ''}
        options={orderFilterOptions.creators}
        inputPlaceholder={TEXTS.search}
        filterOptions={(options) => options}
        ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
        onChange={onSelectChange}
        onInputChange={(_, value, reason) => {
          if (reason !== 'reset') orderFilterOptions.searchCreator(value);
        }}
        sx={getFilterFieldSx()}
      />
      <MaterialScopeFilters
        options={scopeOptions}
        filters={filters}
        onChange={onSelectChange}
        fields={['article']}
        fieldSx={getFilterFieldSx(1.5, 170)}
      />
    </HorizontalForm>
  </Box>
);

export default InternalOrdersFilters;
